/**
 * How To Use Component
 * Displays step-by-step product usage instructions in an accordion
 */

'use client';

import styled from '@emotion/styled';
import { Accordion, useTheme } from '@template-project/afrodita-components';
import { ProductHowToUseStep } from '@/domain/entities/product.entity';

export interface HowToUseProps {
  title?: string;
  steps: ProductHowToUseStep[];
  defaultOpened?: boolean;
}

const Container = styled.section`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: ${({ theme }) => `${theme.space['2xl']} ${theme.space.md}`};
`;

const AccordionWrapper = styled.div`
  width: 100%;
`;

const StepsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.lg};
  padding: ${({ theme }) => `${theme.space.md} 0`};
`;

const StepItem = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.md};
  align-items: flex-start;
`;

const StepNumber = styled.div`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.pallete.primary.color};
  color: ${({ theme }) => theme.colors.pallete.primary.contrast};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  font-size: ${({ theme }) => theme.fontSizes[4]}px;
`;

const StepContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.sm};
`;

const StepTitle = styled.h4`
  font-weight: ${({ theme }) => theme.typography.config.weight.demi};
  font-size: ${({ theme }) => theme.fontSizes[4]}px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const StepDescription = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  line-height: 1.6;
`;

const Title = styled.h3`
  font-size: ${({ theme }) => theme.typography.variants.heading4.fontSize}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.demi};
  margin: 0;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const PlusIcon = styled.span`
  display: inline-block;
  width: 20px;
  height: 20px;
  position: relative;
  
  &::before,
  &::after {
    content: '';
    position: absolute;
    background-color: currentColor;
  }
  
  &::before {
    width: 20px;
    height: 2px;
    top: 9px;
    left: 0;
  }
  
  &::after {
    width: 2px;
    height: 20px;
    top: 0;
    left: 9px;
  }
`;

const MinusIcon = styled.span`
  display: inline-block;
  width: 20px;
  height: 2px;
  background-color: currentColor;
`;

export function HowToUse({
  title = 'How To Use',
  steps,
  defaultOpened = false,
}: HowToUseProps) {
  const theme = useTheme();

  if (!steps || steps.length === 0) {
    return null;
  }

  return (
    <Container>
      <AccordionWrapper>
        <Accordion
          closeIcon={MinusIcon}
          content={
            <StepsWrapper>
              {steps.map((step) => (
                <StepItem key={step.id}>
                  <StepNumber>{step.step_number}</StepNumber>
                  <StepContent>
                    <StepTitle>{step.title}</StepTitle>
                    <StepDescription>{step.description}</StepDescription>
                  </StepContent>
                </StepItem>
              ))}
            </StepsWrapper>
          }
          defaultOpen={defaultOpened}
          header={<Title>{title}</Title>}
          openIcon={PlusIcon}
          variant="simple"
        />
      </AccordionWrapper>
    </Container>
  );
}

